/**
 * Wizard Question Group Component
 * Phase 4 - Smart Grid with Wizard Grouping
 * 
 * Features:
 * - Visual grouping of wizard questions
 * - Hover highlighting for entire group
 * - Click-to-open wizard functionality
 * - Resume wizard button
 * - Progress indicator per wizard
 */

import React, { useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { 
  Wand2, ChevronDown, ChevronUp, Play, CheckCircle2, 
  Circle, AlertCircle, Eye
} from 'lucide-react';

interface WizardQuestion {
  questionId: string;
  questionText: string;
  answer?: string;
  answered: boolean;
  required: boolean;
  pageNumber?: number;
}

interface WizardQuestionGroupProps {
  wizardId: string;
  wizardName: string;
  questions: WizardQuestion[];
  totalPages?: number;
  sessionExists: boolean;
  sessionComplete: boolean;
  currentPage?: number;
  onOpenWizard: (wizardId: string) => void;
  onViewAnswers?: (wizardId: string) => void;
  children?: React.ReactNode; // Question cards to render
  isHighlighted?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function WizardQuestionGroup({
  wizardId,
  wizardName,
  questions,
  totalPages = 1,
  sessionExists,
  sessionComplete,
  currentPage = 0,
  onOpenWizard,
  onViewAnswers,
  children,
  isHighlighted = false,
  onMouseEnter,
  onMouseLeave
}: WizardQuestionGroupProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const answeredCount = questions.filter(q => q.answered).length;
  const totalQuestions = questions.length;
  const requiredCount = questions.filter(q => q.required).length;
  const answeredRequiredCount = questions.filter(q => q.required && q.answered).length;
  const progressPercentage = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;

  const getStatusBadge = () => {
    if (sessionComplete) {
      return (
        <Badge className="bg-green-100 text-green-700 border-green-200">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Complete
        </Badge>
      );
    }
    
    if (sessionExists && !sessionComplete) {
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-200">
          <AlertCircle className="w-3 h-3 mr-1" />
          In Progress (Page {currentPage}/{totalPages})
        </Badge>
      );
    }

    if (answeredCount === 0) {
      return (
        <Badge variant="outline" className="text-gray-600">
          <Circle className="w-3 h-3 mr-1" />
          Not Started
        </Badge>
      );
    }

    return (
      <Badge className="bg-blue-100 text-blue-700 border-blue-200">
        {answeredCount}/{totalQuestions} Answered
      </Badge>
    );
  };

  const getActionButton = () => {
    if (sessionComplete) {
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onViewAnswers?.(wizardId)}
          className="flex items-center gap-1.5"
        >
          <Eye className="w-4 h-4" />
          Review Answers
        </Button>
      );
    }

    if (sessionExists && !sessionComplete) {
      return (
        <Button
          size="sm"
          onClick={() => onOpenWizard(wizardId)}
          className="bg-indigo-600 hover:bg-indigo-700 flex items-center gap-1.5"
        >
          <Play className="w-4 h-4" />
          Resume Wizard
        </Button>
      );
    }

    return (
      <Button
        size="sm"
        onClick={() => onOpenWizard(wizardId)}
        className="bg-indigo-600 hover:bg-indigo-700 flex items-center gap-1.5"
      >
        <Wand2 className="w-4 h-4" />
        Start Wizard
      </Button>
    );
  };

  return (
    <div
      className={`rounded-lg border-2 transition-all duration-200 ${
        isHighlighted 
          ? 'border-indigo-400 bg-indigo-50 shadow-md' 
          : 'border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50'
      }`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Wizard Group Header */}
      <div className="p-4 border-b border-indigo-200 bg-white/50">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Wand2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
              <h3 
                className="font-semibold text-indigo-900 cursor-pointer hover:text-indigo-700 transition-colors"
                onClick={() => onOpenWizard(wizardId)}
              >
                {wizardName}
              </h3>
              {getStatusBadge()}
            </div>

            {/* Progress Bar */}
            <div className="space-y-1.5">
              <Progress 
                value={progressPercentage} 
                className="h-2 bg-indigo-100" 
              />
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span>
                  {answeredCount} of {totalQuestions} questions answered
                  {requiredCount > 0 && (
                    <span className="ml-2 text-orange-600">
                      ({answeredRequiredCount}/{requiredCount} required)
                    </span>
                  )}
                </span>
                <span className="font-medium text-indigo-600">
                  {Math.round(progressPercentage)}%
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {getActionButton()}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2"
            >
              {isExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Wizard Info (collapsed state) */}
        {!isExpanded && (
          <div className="mt-2 pt-2 border-t border-indigo-100 text-xs text-gray-600">
            {sessionExists && !sessionComplete && (
              <span className="text-amber-600 flex items-center gap-1">
                <AlertCircle className="size-3" /> Wizard in progress — select to resume
              </span>
            )}
            {sessionComplete && (
              <span className="text-green-600 flex items-center gap-1">
                <CheckCircle2 className="size-3" /> All wizard questions answered
              </span>
            )}
            {!sessionExists && answeredCount === 0 && (
              <span className="flex items-center gap-1">
                <Wand2 className="size-3" /> Guided wizard available — select to begin
              </span>
            )}
            {!sessionExists && answeredCount > 0 && !sessionComplete && (
              <span>
                {answeredCount} questions answered - {totalQuestions - answeredCount} remaining
              </span>
            )}
          </div>
        )}
      </div>

      {/* Question Cards (children) */}
      {isExpanded && (
        <div className="p-4 space-y-3 bg-white/30">
          {children || (
            <div className="text-center py-4 text-sm text-gray-500">
              Click "Start Wizard" to begin answering questions
            </div>
          )}
        </div>
      )}
    </div>
  );
}